import { useEffect, useState } from 'react';
import { Organization } from 'src/types/models/Organization';
import { Alert, Button, Paper, Progress, Table } from '@mantine/core';
import useTranslation from 'src/i18next.config';
import { useOrganizationService } from 'src/services/organization';
import { useNavigate } from 'react-router-dom';
import { ERoutes } from 'src/types/enum/routes.enum';
import { showNotification } from '@mantine/notifications';
import ConfirmationModal from '../../core/ConfirmationModals';

function OrganizationsList() {
    const organizationService = useOrganizationService();
    const { t } = useTranslation();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [errorMessage] = useState<string | null>();
    const organizationsArray = organizations ? organizations : [];
    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOrgId, setSelectedOrgId] = useState('');

    useEffect(function () {
        if (organizations.length < 1) {
            organizationService.getOrganizations({
                error: error => {
                    console.error(error);
                },
                success: res => {
                    setOrganizations(res);
                },
            });
        }
    }, []);

    function cleanDisplayNumber(value: number): number {
        if (value % 1) {
            return parseFloat(value.toFixed(2));
        } else {
            return value;
        }
    }
    function displayElapsedTime(organization: Organization) {
        if (
            organization.enabledExecutionTime &&
            !isNaN(organization.maxExecutionTime) &&
            organization.maxExecutionTime > 0
        ) {
            return (
                <>
                    <span>
                        {cleanDisplayNumber(organization.usedExecutionTime / 60)}/
                        {cleanDisplayNumber(organization.maxExecutionTime / 60)}
                    </span>
                    <Progress value={(organization.usedExecutionTime / organization.maxExecutionTime) * 100} />
                </>
            );
        }
    }

    function displayNumberOfTimes(organization: Organization) {
        if (organization.enabledMaxExecutions && !isNaN(organization.maxExecutions) && organization.maxExecutions > 0) {
            return (
                <>
                    <span>
                        {organization.usedExecutions}/{organization.maxExecutions}
                    </span>
                    <Progress value={(organization.usedExecutions / organization.maxExecutions) * 100} />
                </>
            );
        }
    }

    function deleteOrganization(id: string) {
        organizationService.deleteOrganization(
            {
                error: error => {
                    showNotification({
                        title: t('organization.deleteOrganizationNotification'),
                        message: error ? error.message : t('organization.deleteOrganizationErrorMessage'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('organization.deleteOrganizationNotification'),
                        message: t('organization.deleteOrganizationSuccessMessage'),
                        color: 'green',
                    });
                    setOrganizations(organizations.filter(org => org._id !== id));
                },
            },
            id
        );
    }

    const rows = organizationsArray.map((organization: Organization) => {
        return (
            <Table.Tr
                key={organization._id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/admin/organizations/${organization._id}`)}
            >
                <Table.Td>{organization.name}</Table.Td>
                <Table.Td>{organization._id}</Table.Td>
                <Table.Td>{organization.resetDay}</Table.Td>
                <Table.Td>{displayElapsedTime(organization)}</Table.Td>
                <Table.Td>{displayNumberOfTimes(organization)}</Table.Td>
                <Table.Td>
                    <Button
                        color="cyan.8"
                        variant="outline"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/organizations/${organization._id}/edit`);
                        }}
                    >
                        {t('organization.editOrganizationBtn')}
                    </Button>
                </Table.Td>
                <Table.Td>
                    <Button
                        color="red"
                        variant="outline"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrgId(organization._id);
                            setIsModalOpen(true);
                        }}
                    >
                        {t('organization.deleteOrganization')}
                    </Button>
                </Table.Td>
            </Table.Tr>
        );
    });

    return (
        <>
            <Button
                color="cyan.8"
                type="submit"
                style={{ marginBottom: 20 }}
                onClick={() => {
                    navigate(ERoutes.ADMIN_ADD_ORGANIZATION);
                }}
            >
                {t('organization.addOrganizationBtn')}
            </Button>
            <Paper shadow="md" style={{ width: 'fit-content', margin: 'auto' }}>
                <Table.ScrollContainer minWidth={700}>
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{ minWidth: '120px' }}>{t('Name')}</Table.Th>
                                <Table.Th>{'ID'}</Table.Th>
                                <Table.Th style={{ minWidth: '65px' }}>{t('resetDay')}</Table.Th>
                                <Table.Th style={{ minWidth: '250px' }}>{t('usedExecutionTime')}</Table.Th>
                                <Table.Th style={{ minWidth: '200px' }}>{t('usedExecutions')}</Table.Th>
                                <Table.Th>{t('actions')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>{rows}</Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>
            <ConfirmationModal
                isOpen={isModalOpen}
                title={t('organization.deleteOrganization')}
                text={t('areYouSure')}
                onClose={() => setIsModalOpen(false)}
                onConfirmation={() => {
                    setIsModalOpen(false);
                    deleteOrganization(selectedOrgId);
                }}
            />
            {errorMessage && (
                <Alert color="red" radius="lg" variant="filled" style={{ marginTop: 10 }}>
                    {errorMessage}
                </Alert>
            )}
        </>
    );
}

export default OrganizationsList;
